import React, { useMemo, useState, useRef } from 'react';
import { leadZero } from '../../../../../../../../utils';

interface DonutChartProps {
    values: number[];
    quantidades: number[];
    size?: number;
}

const COLORS = [
    '#7ebc61',
    '#febc2f',
    '#db4840',
    '#F5F5F5',
];

const LABELS = ['No prazo', 'Adiantado', 'Atrasado', 'Não iniciado'];

export const DonutChartPrazosSvg: React.FC<DonutChartProps> = ({ values, quantidades, size = 150 }) => {

    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
    const containerRef = useRef<HTMLDivElement>(null);

    const strokeWidth = size * 0.185;
    const gapPixels = size * 0.05;

    const radius = (size - strokeWidth) / 2;
    const center = size / 2;
    const circumference = 2 * Math.PI * radius;
    const total = values.reduce((acc, val) => acc + val, 0);
    const totalQuantidades = (quantidades[0] ?? 0) + (quantidades[1] ?? 0) + (quantidades[2] ?? 0);
    const dominantIndex = [0, 1, 2].reduce((max, i) => (quantidades[i] ?? 0) > (quantidades[max] ?? 0) ? i : max, 0);
    const dominantColor = COLORS[dominantIndex];

    const segments = useMemo(() => {
        if (total === 0) return [];

        const minAllocatedLength = strokeWidth + gapPixels;
        const rawLengths = values.map(v => (v / total) * circumference);

        let deficit = 0;
        const adjustedLengths = [...rawLengths];
        const largeIndices: number[] = [];

        adjustedLengths.forEach((len, i) => {
            if (len < minAllocatedLength) {
                deficit += (minAllocatedLength - len);
                adjustedLengths[i] = minAllocatedLength;
            } else {
                largeIndices.push(i);
            }
        });

        if (deficit > 0 && largeIndices.length > 0) {
            const totalLargeLength = largeIndices.reduce((sum, i) => sum + rawLengths[i], 0);
            largeIndices.forEach(i => {
                const ratio = rawLengths[i] / totalLargeLength;
                adjustedLengths[i] -= deficit * ratio;
            });
        }

        let accumulatedLength = 0;

        return adjustedLengths.map((allocatedLength, index) => {
            const strokeLength = Math.max(0, allocatedLength - gapPixels - strokeWidth);
            const startOffset = accumulatedLength + (gapPixels / 2) + (strokeWidth / 2);
            const rotationDeg = (startOffset / circumference) * 360;

            // Midpoint angle for label positioning
            const midOffset = accumulatedLength + (allocatedLength / 2);
            const midAngleDeg = (midOffset / circumference) * 360 - 90;
            const midAngleRad = (midAngleDeg * Math.PI) / 180;

            accumulatedLength += allocatedLength;

            return {
                color: COLORS[index % COLORS.length],
                strokeDasharray: `${strokeLength} ${circumference}`,
                rotation: rotationDeg - 90,
                originalValue: values[index],
                midAngleRad,
            };
        });
    }, [values, total, circumference, gapPixels, strokeWidth]);

    // Compute label positions with collision avoidance
    const labelPositions = useMemo(() => {
        if (segments.length === 0) return [];

        const labelRadius = radius + strokeWidth / 2 + 8;
        const MIN_VERTICAL_GAP = 30; // minimum px between label centers

        // Compute initial positions based on segment midpoint angles
        const positions = segments.map((seg, i) => {
            const x = center + labelRadius * Math.cos(seg.midAngleRad);
            const y = center + labelRadius * Math.sin(seg.midAngleRad);
            const isRight = x >= center;
            return { index: i, x, y, isRight, originalY: y };
        });

        // Separate into left and right groups, resolve collisions within each
        const rightLabels = positions.filter(p => p.isRight).sort((a, b) => a.y - b.y);
        const leftLabels = positions.filter(p => !p.isRight).sort((a, b) => a.y - b.y);

        // Spread labels vertically if too close
        const spreadLabels = (labels: typeof positions) => {
            for (let pass = 0; pass < 5; pass++) {
                for (let i = 1; i < labels.length; i++) {
                    const gap = labels[i].y - labels[i - 1].y;
                    if (gap < MIN_VERTICAL_GAP) {
                        const adjust = (MIN_VERTICAL_GAP - gap) / 2;
                        labels[i - 1].y -= adjust;
                        labels[i].y += adjust;
                    }
                }
            }
        };

        spreadLabels(rightLabels);
        spreadLabels(leftLabels);

        // Merge back, indexed by original index
        const result: { x: number; y: number; isRight: boolean }[] = [];
        [...rightLabels, ...leftLabels].forEach(p => {
            result[p.index] = { x: p.x, y: p.y, isRight: p.isRight };
        });
        return result;
    }, [segments, center, radius, strokeWidth]);

    const handleMouseMove = (e: React.MouseEvent, index: number) => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setTooltipPos({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            });
            setHoveredIndex(index);
        }
    };

    const handleMouseLeave = () => {
        setHoveredIndex(null);
    };

    return (
        <div
            ref={containerRef}
            style={{ width: size, height: size, position: 'relative', marginTop: 25 }}
        >
            <svg
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                onMouseLeave={handleMouseLeave}
                style={{ overflow: 'visible' }}
            >
                {total === 0 && (
                    <circle
                        cx={center}
                        cy={center}
                        r={radius}
                        fill="none"
                        stroke="#f5f5f5"
                        strokeWidth={strokeWidth}
                    />
                )}
                {segments.map((seg, i) => {
                    if (!seg) return null;
                    const isHovered = hoveredIndex === i;

                    return (
                        <circle
                            key={`prazos-segment-${i}`}
                            cx={center}
                            cy={center}
                            r={radius}
                            fill="none"
                            stroke={seg.originalValue === 0 ? "#f5f5f5" : seg.color}
                            strokeWidth={strokeWidth}
                            strokeLinecap="round"
                            strokeDasharray={seg.strokeDasharray}
                            strokeDashoffset={0}
                            transform={`rotate(${seg.rotation} ${center} ${center})`}
                            onMouseMove={(e) => handleMouseMove(e, i)}
                            style={{
                                transition: 'all 0.6s ease',
                                cursor: 'pointer',
                                opacity: hoveredIndex !== null && !isHovered ? 0.4 : 1,
                                filter: brightness(isHovered, hoveredIndex, seg.originalValue),
                                transformOrigin: 'center',
                                transform: isHovered
                                    ? `rotate(${seg.rotation}deg) scale(1.02)`
                                    : `rotate(${seg.rotation}deg) scale(1)`
                            }}
                        />
                    );
                })}

                {/* Total sum in center */}
                <text
                    x="50%"
                    y="50%"
                    dy=".35em"
                    textAnchor="middle"
                    fill={dominantColor}
                    style={{ fontWeight: 800, fontSize: 32, transition: 'fill 0.6s ease' }}
                >
                    {leadZero(totalQuantidades)}
                </text>
            </svg>

            {/* Labels dynamically positioned next to each segment */}
            {labelPositions.map((pos, i) => {
                if (!pos || quantidades[i] === 0) return null;
                const color = COLORS[i];
                return (
                    <div
                        key={`label-${i}`}
                        style={{
                            position: 'absolute',
                            left: pos.x,
                            top: pos.y,
                            transform: pos.isRight
                                ? 'translate(4px, -50%)'
                                : 'translate(calc(-100% - 4px), -50%)',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                            textAlign: pos.isRight ? 'left' : 'right',
                            transition: 'left 0.6s ease, top 0.6s ease',
                        }}
                    >
                        <div style={{ color, fontWeight: 700, fontSize: 16 }}>
                            {leadZero(quantidades[i])}
                        </div>
                        <div style={{ color, fontWeight: 500, fontSize: 12, marginTop: -10 }}>
                            {LABELS[i]}
                        </div>
                    </div>
                );
            })}

            {/* Floating Tooltip */}
            {hoveredIndex !== null && (
                <div
                    style={{
                        position: 'absolute',
                        left: tooltipPos.x,
                        top: tooltipPos.y,
                        transform: 'translate(15px, 15px)',
                        background: 'rgba(0, 0, 0, 0.8)',
                        padding: 7,
                        color: '#fff',
                        fontWeight: 500,
                        borderRadius: 5,
                        fontSize: 13,
                        zIndex: 200,
                        pointerEvents: 'none',
                    }}
                >
                    <span style={{ margin: 0, whiteSpace: 'nowrap' }}>
                        {LABELS[hoveredIndex]}: {Math.round((values[hoveredIndex] / total) * 100)}%
                    </span>
                </div>
            )}
        </div>
    );
};

function brightness(isHovered: boolean, hoveredIndex: number, value: number) {
    if (!isHovered) return 'none';
    if (hoveredIndex === 3) return 'none';
    if (value === 0) return 'none';
    return 'brightness(110%)';
}